import { useEffect, useMemo, useState } from "react";
import { Polygon } from "react-leaflet";
import { useEditOnboarding } from "../../hooks/useEditOnboarding";
import axios from "../../services/axios";
import {
  DropdownV2,
  FormCardV2,
  FormGridV2,
  InputV2,
  MultiDropdownV2,
  OptionV2,
} from "../FormControlsV2";
import { FooterEditOnboarding } from "../EditOnboarding/Footer";
import { FlyToBounds } from "../Leaflet/FlyToBounds/Index";
import { LeafletStaticMap } from "../Leaflet/LeafletStaticMap/Index";
import Constants from "../../constants/Index";
import { ICoords } from "../../@types/GoogleMaps/ICoord";
import { useTranslation } from "react-i18next";

type EvaluatorFormValue = {
  evaluator: OptionV2 | null;
  farm: string;
  area: OptionV2 | null;
  microAreas: OptionV2[];
};

type EvaluatorFormErrors = Partial<Record<keyof EvaluatorFormValue, string>>;

const emptyEvaluatorForm: EvaluatorFormValue = {
  evaluator: null,
  farm: "",
  area: null,
  microAreas: [],
};

function normalizeCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;
  return [];
}

export default function CreateEvaluator() {
  const { t } = useTranslation();
  const [allResponsibles, setAllResponsibles] = useState<OptionV2[]>([]);
  const [activeEvaluatorUserIds, setActiveEvaluatorUserIds] = useState<Set<string>>(new Set());
  const [optionsArea, setOptionsArea] = useState<OptionV2[]>([]);
  const [optionsMicroAreas, setOptionsMicroAreas] = useState<OptionV2[]>([]);
  const [formValue, setFormValue] = useState<EvaluatorFormValue>(emptyEvaluatorForm);
  const [errors, setErrors] = useState<EvaluatorFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const {
    defaultValue,
    idSelected,
    setIdSelected,
    setDefaultValue,
    setOptionForm,
  } = useEditOnboarding();

  const isEditing = !!idSelected;
  const selectedResponsible = formValue.evaluator;
  const selectedArea = formValue.area;
  const selectedAreaCoords = normalizeCoords(selectedArea?.info?.map_coords);
  const selectedMicroAreaPolygons = formValue.microAreas
    .map((microArea) => ({
      id: microArea.value,
      coords: normalizeCoords(microArea.info?.map_coords),
    }))
    .filter((microArea) => microArea.coords.length > 0);
  const selectedMicroAreasCoords = selectedMicroAreaPolygons.flatMap((microArea) => microArea.coords);
  const fitCoords = selectedAreaCoords.length ? selectedAreaCoords : selectedMicroAreasCoords;

  const responsibleOptions = useMemo(() => {
    return allResponsibles.filter((responsible) => {
      if (String(responsible.value) === String(selectedResponsible?.value)) return false;
      return !activeEvaluatorUserIds.has(String(responsible.value));
    });
  }, [activeEvaluatorUserIds, allResponsibles, selectedResponsible]);

  const areaOptions = useMemo(() => {
    if (!selectedArea) return optionsArea;
    return optionsArea.filter((area) => area.value !== selectedArea.value);
  }, [optionsArea, selectedArea]);

  function closeForm() {
    setOptionForm(undefined);
    setDefaultValue(undefined);
    setIdSelected(0);
  }

  function validate(value = formValue) {
    const nextErrors: EvaluatorFormErrors = {};
    if (!value.evaluator) nextErrors.evaluator = t("formsV2.responsibleRequired");
    if (!value.area) nextErrors.area = t("formsV2.areaRequired");
    if (!value.microAreas.length) nextErrors.microAreas = t("formsV2.paddocksRequired");
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  useEffect(() => {
    Promise.all([
      axios.get("areas"),
      axios.get("users/user/evaluator"),
    ])
      .then(([areasResponse, evaluatorsResponse]) => {
        setAllResponsibles(areasResponse.data.map((area: any) => ({
          label: area.manager_user?.name,
          value: area.manager_user?.id,
          info: area
        })).filter((option: OptionV2) => option.label && option.value));
        setActiveEvaluatorUserIds(new Set(
          evaluatorsResponse.data
            .filter((evaluator: any) => evaluator.status === "ACTIVE")
            .map((evaluator: any) => String(evaluator.user_id)),
        ));
      });
  }, []);

  function onSubmit() {
    if (!validate()) return;

    const body = {
      user_id: Number(formValue.evaluator?.value),
      area_ids: [Number(formValue.area?.value)],
      micro_area_ids: formValue.microAreas.map((microArea) => Number(microArea.value)),
    };

    setSubmitting(true);
    const request = idSelected
      ? axios.patch("users/user/evaluator", body)
      : axios.post("users/user/evaluator", body);

    request
      .then(closeForm)
      .catch((err) => console.error(err))
      .finally(() => setSubmitting(false));
  }

  function deleteEvaluator(id: number) {
    setSubmitting(true);
    axios.delete(`users/user/evaluator/${id}`)
      .then(closeForm)
      .catch(err => console.error(err))
      .finally(() => setSubmitting(false));
  }

  useEffect(() => {
    if (defaultValue) {
      const evaluator = {
        label: defaultValue?.area?.manager_user?.name,
        value: defaultValue?.area?.manager_user?.id
      };
      const farm = defaultValue?.area?.farm
        ? `${defaultValue?.area?.farm?.name} (${defaultValue?.area?.farm?.initials})`
        : "";
      const area = {
        label: defaultValue?.area?.name,
        value: defaultValue?.area?.id,
        info: defaultValue?.area,
      };

      setFormValue({ evaluator, farm, area, microAreas: [] });
      setOptionsArea([{
        label: defaultValue?.area?.name,
        value: defaultValue?.area?.id,
        info: defaultValue?.area,
      }]);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (!selectedArea) {
      setOptionsMicroAreas([]);
      setFormValue((current) =>
        current.microAreas.length ? { ...current, microAreas: [] } : current
      );
      return;
    }

    let active = true;
    axios.get("/micro-areas", { params: { area_id: selectedArea.value } })
      .then(({ data }) => {
        if (!active) return;

        const options = data.map((microArea: any) => ({
          label: microArea.initials,
          value: microArea.id,
          info: microArea,
        }));
        const savedIds = new Set(
          (defaultValue?.micro_area_ids || defaultValue?.micro_areas || [])
            .map((microArea: any) => String(microArea?.id ?? microArea)),
        );
        const selected = savedIds.size
          ? options.filter((option: OptionV2) => savedIds.has(String(option.value)))
          : isEditing
            ? options
            : [];

        setOptionsMicroAreas(options);
        setFormValue((current) => ({ ...current, microAreas: selected }));
      })
      .catch((err) => {
        if (!active) return;
        console.error(err);
        setOptionsMicroAreas([]);
        setFormValue((current) => ({ ...current, microAreas: [] }));
      });

    return () => {
      active = false;
    };
  }, [defaultValue, isEditing, selectedArea]);

  return (
    <>
      <main className="body__formEditOnboarding create-evaluator-v2">
        <form onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}>
          <FormCardV2>
            <div className="create-evaluator-v2__layout">
              <FormGridV2 columns={1}>
                <DropdownV2
                  name="evaluator"
                  label={t("formsV2.responsible")}
                  value={formValue.evaluator}
                  options={responsibleOptions}
                  placeholder={t("formsV2.selectResponsible")}
                  searchable
                  disabled={isEditing}
                  error={errors.evaluator}
                  emptyMessage={t("formsV2.noResponsible")}
                  onChange={(evaluator) => {
                    const areaInfo = evaluator?.info;
                    const nextArea = areaInfo ? { label: areaInfo.name, value: areaInfo.id, info: areaInfo } : null;
                    const farm = areaInfo?.farm ? `${areaInfo.farm.name}` : "";
                    setOptionsArea(nextArea ? [nextArea] : []);
                    setFormValue({ evaluator, farm, area: null, microAreas: [] });
                    setOptionsMicroAreas([]);
                    setErrors((current) => ({ ...current, evaluator: undefined }));
                  }}
                />

                <InputV2
                  name="farm"
                  label={t("formsV2.farm")}
                  value={formValue.farm}
                  disabled
                  onChange={() => undefined}
                />

                <DropdownV2
                  name="area"
                  label={t("formsV2.area")}
                  value={formValue.area}
                  options={areaOptions}
                  placeholder={t("formsV2.selectArea")}
                  searchable
                  error={errors.area}
                  emptyMessage={t("formsV2.noAreas")}
                  onChange={(area) => {
                    setFormValue((current) => ({ ...current, area, microAreas: [] }));
                    setErrors((current) => ({ ...current, area: undefined }));
                  }}
                />

                <MultiDropdownV2
                  name="microAreas"
                  label={t("formsV2.paddocks")}
                  value={formValue.microAreas}
                  options={optionsMicroAreas}
                  placeholder={t("formsV2.selectPaddocks")}
                  searchable
                  selectAll
                  disabled={!formValue.area}
                  error={errors.microAreas}
                  emptyMessage={t("formsV2.noPaddocksAvailable")}
                  onChange={(microAreas) => {
                    setFormValue((current) => ({ ...current, microAreas }));
                    setErrors((current) => ({ ...current, microAreas: undefined }));
                  }}
                />
              </FormGridV2>

              <div className="create-evaluator-v2__map-column">
                <div className="create-evaluator-v2__map">
                  {fitCoords.length > 0 ? (
                    <LeafletStaticMap>
                      <FlyToBounds fitCoords={fitCoords}>
                        {selectedMicroAreaPolygons.map((microArea) => (
                          <Polygon
                            key={`${microArea.id}`}
                            positions={microArea.coords}
                            {...Constants.LEAFLET_NORMAL_BLUE}
                          />
                        ))}
                        {selectedAreaCoords.length > 0 && (
                          <Polygon
                            positions={selectedAreaCoords}
                            {...Constants.LEAFLET_DASHED_GREEN}
                          />
                        )}
                      </FlyToBounds>
                    </LeafletStaticMap>
                  ) : (
                    <div className="create-evaluator-v2__map-empty">
                      <strong>{t("formsV2.paddocksMap")}</strong>
                      <span>{t("formsV2.paddocksMapHelp")}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </FormCardV2>
        </form>
      </main>

      <FooterEditOnboarding
        undoFunction={closeForm}
        saveFunction={onSubmit}
        deleteFunction={isEditing ? () => deleteEvaluator(idSelected) : undefined}
        isNew={!isEditing}
        saveLabel={t(isEditing ? "formsV2.confirm" : "formsV2.add")}
        disabledSubmit={submitting}
      />
    </>
  )
}
